import {useIsPayOrderAppPage} from '@aboutyou/router';
import {useIsStorageConsentGiven} from '@aboutyou/storage';
import {
  TADARIDA_OPTIONAL_USER_PERSIST_KEY,
  useOptionalUser,
} from '@aboutyou/user';
import {doAsync, localStorageRemoveItem} from '@aboutyou/utils';
import React, {useEffect} from 'react';
import {AUTH_FLOW_STATE} from '../../features/Authentication/hooks/state';
import {useIsAuthenticated} from '../../features/Authentication/hooks/useIsAuthenticated';
import {LoadCustomerData} from './LoadCustomerData';

export const LoadCustomerDataConditional = () => {
  const {isUserDataLoading, isAuthenticated} = useIsAuthenticated();
  const isStorageConsentGiven = useIsStorageConsentGiven();
  const {isLoading: optionalUserLoading, data: optionalUser} =
    useOptionalUser();
  const optionalUserData = optionalUser?.userData;
  const isPayOrderAppPage = useIsPayOrderAppPage();

  /**
   * This effect is responsible for removing any stale optional user data from the local storage if the user is logged out
   * This can happen in the following case:
   * - User login in one language (DE-DE) and -> switches to another language (EN-DE) -> logs out -> then switches back to the original language (DE-DE)
   * - in this case we need to remove the optional user data from the local storage
   *
   * Reload the page to restart the session, like we when user logs out
   */
  useEffect(() => {
    // in pay order app we dont want to force reload if the user is not authenticated, since
    // we want handle a forced login in the pay order app and want the user to be able to login again if needed
    if (isPayOrderAppPage) {
      return;
    }
    if (isUserDataLoading || optionalUserLoading) {
      return;
    }
    if (optionalUserData && !isAuthenticated) {
      localStorageRemoveItem(TADARIDA_OPTIONAL_USER_PERSIST_KEY);
      doAsync(() => {
        window.location.reload();
      });
    }
  }, [
    isAuthenticated,
    isPayOrderAppPage,
    isUserDataLoading,
    optionalUserData,
    optionalUserLoading,
  ]);

  /**
   * If the user is manually loggingIn in this shop session, we dont want to load the customer data from the backend since we already get the optional user data from login endpoints,
   * currently we have no reactish way to know if the user is logged in in this session, or are we reading the login data from cookies.
   * Thus this workaround where AUTH_FLOW_STATE is set to true in the useAuthResponseHandler hook when the user logs in.
   * This prevents additional backend calls to load the customer data when the user login in this session.
   */
  if (AUTH_FLOW_STATE.completedInThisSession) {
    return null;
  }

  if (!isStorageConsentGiven) {
    return null;
  }

  if (isUserDataLoading || optionalUserLoading || optionalUserData) {
    return null;
  }

  return <LoadCustomerData />;
};
